import { getCurrencyFormatter } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RescueModalDiscount } from "@redotech/redo-model/checkouts/predictive-conversion-settings";
import {
  DefaultCheckoutProbabilityBucket,
  getDefaultCheckoutProbabilityBucket,
} from "@redotech/redo-model/checkouts/segments";
import { AutocheckRescuePanelActiveSplit } from "@redotech/redo-model/conversion/conversion-autocheck";
import { RESCUE_PANEL_CTA_CLICKED_KEY } from "@redotech/redo-model/shopify/cart";
import { SlideInPanelAnchorLocation } from "@redotech/redo-model/slide-in-panel/slide-in-panel-anchor-location";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Header, Text } from "@redotech/redo-web/text";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  ANONYMOUS_SHOPPER_ID_KEY,
  RESCUE_PANEL_PREVIEW_QUERY_PARAM,
} from "@redotech/redo-web/utils/shared-conf";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { logRescuePanelAnalytics } from "@redotech/shopify-sdk/rpc/schema/rescue-panel/log-rescue-panel-analytics";
import { isDef } from "@redotech/util/checks";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { StorefrontClient } from "../../client/storefront";
import { log } from "../../monitoring/log";
import { useCart } from "../../utils/cart";
import { goToCheckout } from "../../utils/checkout-buttons/on-click-logic";
import { SlideInPanel } from "../slide-in-panel/slide-in-panel";
import { CheckoutProbabilityContext } from "./checkout-probability-context";
import { useRescuePanelSplit } from "./use-rescue-panel-split";

const SHOWN_LOCAL_STORAGE_KEY = "rrp-dismissed";
const SHOWN_LOCAL_STORAGE_VALUE = "true";
const SHOW_DELAY_MS = 3000;
const PANEL_DISMISSED_EXPIRY_HOURS = 24 * 7; // 7 days

const STALE_PAGE_TIME_JITTER_SECONDS = 30;
const STALE_PAGE_TIME_SECONDS = 90 - STALE_PAGE_TIME_JITTER_SECONDS / 2;

interface ShouldShowPanelParams {
  hasDismissed: boolean;
  cumulativeConversionProbability: number | null;
  previousCumulativeConversionProbability: number | null;
  instantaneousConversionProbability: number | null;
  previousInstantaneousConversionProbability: number | null;
  mountTime: Temporal.Instant;
  cart: ShopifyCart | undefined;
  previewOn: boolean;
}

const shouldShowPanel = (params: ShouldShowPanelParams): boolean => {
  if (params.previewOn) {
    console.warn("Showing rescue panel in preview mode");
    return true;
  }

  if (
    params.hasDismissed ||
    !isDef(params.cumulativeConversionProbability) ||
    !isDef(params.instantaneousConversionProbability) ||
    !isDef(params.previousInstantaneousConversionProbability) ||
    !params.cart?.items.length
  ) {
    log("Not showing rescue panel: ", params);
    return false;
  }

  const now = Temporal.Now.instant();

  // If cart has more than 2 items and has been mounted for more than
  // 90 seconds (with 15 second jitter), show the panel
  const staleTime =
    STALE_PAGE_TIME_SECONDS +
    Math.floor(Math.random() * STALE_PAGE_TIME_JITTER_SECONDS);
  if (
    params.cart.items.reduce((acc, item) => acc + item.quantity, 0) > 2 &&
    now.since(params.mountTime).total({ unit: "seconds" }) > staleTime
  ) {
    return true;
  }

  const bucket = getDefaultCheckoutProbabilityBucket(
    Math.round(params.cumulativeConversionProbability * 100),
  );
  if (
    bucket !== DefaultCheckoutProbabilityBucket.COOL &&
    bucket !== DefaultCheckoutProbabilityBucket.WARM
  ) {
    // Only show for cool and warm buckets
    log("Should not show rescue panel, not in cool or warm bucket: ", bucket);
    return false;
  }

  // If within SHOW_DELAY_MS of mount, don't show
  if (
    now.since(params.mountTime).total({ unit: "milliseconds" }) < SHOW_DELAY_MS
  ) {
    log("Should not show rescue panel, within 3 seconds of mount time");
    return false;
  }

  // Show if instantaneous probability has dropped significantly (more than 50% decrease)
  const currentInstantaneousProb = params.instantaneousConversionProbability;
  const previousInstantaneousProb =
    params.previousInstantaneousConversionProbability;

  const shouldShow = currentInstantaneousProb < previousInstantaneousProb * 0.5;
  log("Should show rescue panel result: ", shouldShow);
  return shouldShow;
};

function formatDiscount(discount: RescueModalDiscount): string {
  if (discount.discountType === "percentage") {
    return `${discount.percentage}%`;
  }

  const formatter = getCurrencyFormatter(discount.amount.currencyCode);
  return formatter.format(parseFloat(discount.amount.value));
}

export const RescuePanel = memo(function RescuePanel({
  storefrontClient,
  discountCode,
  discount,
  autocheckRescuePanelSplit,
  anchorLocation = SlideInPanelAnchorLocation.TOP_LEFT,
}: {
  storefrontClient: StorefrontClient;
  discountCode: string;
  discount: RescueModalDiscount;
  autocheckRescuePanelSplit: AutocheckRescuePanelActiveSplit | null;
  anchorLocation?: SlideInPanelAnchorLocation;
}) {
  const checkoutProbabilityContext = useContext(CheckoutProbabilityContext);
  const shopifyRpcClient = useContext(RedoShopifyRpcClientContext);

  const rescuePanelSplit = useRescuePanelSplit({
    autocheckRescuePanelSplit,
    storefrontClient,
  });

  const previewOn =
    new URLSearchParams(window.location.search).get(
      RESCUE_PANEL_PREVIEW_QUERY_PARAM,
    ) === "true";

  const [panelOpen, setPanelOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(
    () =>
      getLocalStorageWithExpiry(SHOWN_LOCAL_STORAGE_KEY) ===
      SHOWN_LOCAL_STORAGE_VALUE,
  );

  const mountTimeRef = useRef<Temporal.Instant>(Temporal.Now.instant());

  const cart = useCart();

  // Handle checkout button click
  const [checkoutClickLoad, doCheckoutClick] = useTriggerLoad(async (abort) => {
    try {
      const sessionId = getCookie(AllCookies.ShopifySessionId);
      if (!sessionId) {
        console.warn(
          "Session ID not available for recording rescue panel CTA clicked",
        );
      }

      await Promise.all([
        ...(rescuePanelSplit.splitActive
          ? [rescuePanelSplit.onPanelCtaClicked()]
          : []),
        storefrontClient.updateCartAttribute({
          attributes: [{ key: RESCUE_PANEL_CTA_CLICKED_KEY, value: "true" }],
        }),
        storefrontClient.applyDiscountCode(discountCode, abort),
        ...(sessionId && shopifyRpcClient
          ? [logRescuePanelCtaClicked({ shopifyRpcClient, sessionId, cart })]
          : []),
      ]);

      // Navigate to checkout
      goToCheckout();
    } catch (error) {
      console.error("Failed to navigate to checkout:", error);
    }
  });

  // Handle show rescue panel
  const [_, doShowPanel] = useTriggerLoad(async (abort) => {
    const sessionId = getCookie(AllCookies.ShopifySessionId);
    if (!sessionId) {
      console.warn(
        "Session ID not available for recording rescue panel viewed",
      );
    } else if (shopifyRpcClient) {
      const localStorageKey = constructPanelViewedLocalStorageKey({
        sessionId,
      });

      const hasAlreadyRecorded = getLocalStorageWithExpiry(localStorageKey);

      if (!hasAlreadyRecorded) {
        setLocalStorageWithExpiry(localStorageKey, "true");

        try {
          const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);
          const shopifyCustomerId =
            (globalThis as any).ShopifyAnalytics?.meta?.page?.customerId ??
            null;

          const itemQuantity =
            cart?.items.reduce((acc, item) => acc + item.quantity, 0) ?? 0;

          await shopifyRpcClient(logRescuePanelAnalytics, {
            sessionId,
            type: "viewed",
            shopifyCustomerId,
            anonymousId: anonymousId ?? undefined,
            itemQuantity,
          });
        } catch (err) {
          console.error("Failed to record rescue panel viewed", err);
        }
      }
    }

    setPanelOpen(true);
  });

  const handleCheckoutClick = useHandler(() => {
    doCheckoutClick();
  });

  const onShowPanel = useHandler(() => {
    doShowPanel();
  });

  const onClosePanel = useHandler(() => {
    setPanelOpen(false);
    setHasDismissed(true);

    try {
      setLocalStorageWithExpiry(
        SHOWN_LOCAL_STORAGE_KEY,
        SHOWN_LOCAL_STORAGE_VALUE,
        PANEL_DISMISSED_EXPIRY_HOURS,
      );
    } catch (error) {
      console.warn(
        "Failed to save rescue panel state to local storage:",
        error,
      );
    }
  });

  // Monitor checkout probability changes and show panel when conditions are met
  useEffect(() => {
    if (!checkoutProbabilityContext) {
      return;
    }

    const showPanelParams: ShouldShowPanelParams = {
      hasDismissed,
      cumulativeConversionProbability:
        checkoutProbabilityContext.cumulativeConversionProbability,
      previousCumulativeConversionProbability:
        checkoutProbabilityContext.previousCumulativeConversionProbability,
      instantaneousConversionProbability:
        checkoutProbabilityContext.instantaneousHazardProbability,
      previousInstantaneousConversionProbability:
        checkoutProbabilityContext.previousInstantaneousHazardProbability,
      mountTime: mountTimeRef.current,
      cart,
      previewOn,
    };

    if (!shouldShowPanel(showPanelParams)) {
      return;
    }

    if (rescuePanelSplit.splitActive) {
      rescuePanelSplit.onShouldShowPanel()?.catch(console.error);
    }

    onShowPanel();
  }, [
    checkoutProbabilityContext,
    hasDismissed,
    rescuePanelSplit,
    onShowPanel,
    cart,
    previewOn,
  ]);

  const discountText = formatDiscount(discount);

  if (
    rescuePanelSplit.splitActive &&
    !rescuePanelSplit.panelAllowedForTreatment &&
    !previewOn
  ) {
    return null;
  }

  return (
    <SlideInPanel
      anchorLocation={anchorLocation}
      handleCloseClick={onClosePanel}
      open={panelOpen}
    >
      <Flex align="flex-start" dir="column" gap="xl" w="full">
        <Flex align="flex-start" dir="column" gap="xl">
          <Header fontSize="sm" fontWeight="bold" textColor="primary">
            Your {discountText} discount is waiting
          </Header>
          <Text fontSize="md" fontWeight="regular" textColor="primary">
            Go to checkout with the button below to complete your purchase and
            save on this order.
          </Text>
        </Flex>

        <Flex align="center" gap="xl" w="full">
          <Flex flex={1}>
            <RedoButton
              flexGrow={1}
              hierarchy="primary"
              onClick={handleCheckoutClick}
              pending={checkoutClickLoad.pending}
              size="xl"
              text="Apply discount"
            />
          </Flex>
        </Flex>
      </Flex>
    </SlideInPanel>
  );
});

const VIEWED_LOCAL_STORAGE_KEY_PREFIX = "rrp-viewed";

function constructPanelViewedLocalStorageKey({
  sessionId,
}: {
  sessionId: string;
}): `${typeof VIEWED_LOCAL_STORAGE_KEY_PREFIX}-${string}` {
  return `${VIEWED_LOCAL_STORAGE_KEY_PREFIX}-${sessionId}`;
}

async function logRescuePanelCtaClicked({
  shopifyRpcClient,
  sessionId,
  cart,
}: {
  shopifyRpcClient: RedoShopifyRpcClient;
  sessionId: string;
  cart: ShopifyCart | undefined;
}) {
  try {
    const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);
    const shopifyCustomerId =
      (globalThis as any).ShopifyAnalytics?.meta?.page?.customerId ?? null;

    const itemQuantity =
      cart?.items.reduce((acc, item) => acc + item.quantity, 0) ?? 0;

    await shopifyRpcClient(logRescuePanelAnalytics, {
      sessionId,
      type: "clicked",
      shopifyCustomerId,
      anonymousId: anonymousId ?? undefined,
      itemQuantity,
    });
  } catch (err) {
    console.error("Failed to record rescue panel CTA clicked", err);
  }
}
